// commandCenterService.js - API client for Command Center feature
// Handles all command center related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Get complete Command Center summary.
 * Includes summary stats, all assets (each carrying trajectory/delta/trend/driver
 * per PRD-68), top risks, trends, drift, and a `meta` diagnostics block
 * (PRD-67) including `meta.assetsTrajectory`.
 *
 * @param {number} [trajectoryWindowDays=7] - 7 | 30 | 90. Controls the per-asset
 *   trajectory + delta + driver-hint window. Invalid values are clamped to 7
 *   server-side.
 * @returns {Promise<object>} - { success, summary, assets, topRisks, trends, meta }
 */
export async function getCommandCenterSummary(trajectoryWindowDays = 7) {
    try {
        const headers = await getHeaders();

        const params = new URLSearchParams();
        if (trajectoryWindowDays != null) {
            params.set('trajectoryWindowDays', String(trajectoryWindowDays));
        }
        const qs = params.toString();
        const url = qs
            ? `${API_BASE_URL}/command-center-summary?${qs}`
            : `${API_BASE_URL}/command-center-summary`;

        const response = await fetch(url, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get command center summary (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting command center summary:', error);
        throw error;
    }
}

/**
 * Get risk level color based on risk level string.
 * Uses semantic CSS variables (--risk-*) that are defined per-theme
 * in themeVariations.css, ensuring correct colors across all 10 themes.
 * @param {string} riskLevel - 'critical', 'atRisk', 'caution', 'healthy'
 * @returns {string} - CSS color value
 */
export function getRiskLevelColor(riskLevel) {
    const colors = {
        critical: 'var(--risk-critical, var(--severity-critical))',
        atRisk: 'var(--risk-high, var(--severity-high))',
        caution: 'var(--risk-caution, var(--severity-medium))',
        healthy: 'var(--risk-healthy, var(--severity-low))'
    };
    return colors[riskLevel] || 'var(--text-secondary)';
}

/**
 * Get risk level label for display
 * @param {string} riskLevel - 'critical', 'atRisk', 'caution', 'healthy'
 * @returns {string} - Human-readable label
 */
export function getRiskLevelLabel(riskLevel) {
    const labels = {
        critical: 'Critical',
        atRisk: 'At Risk',
        caution: 'Caution',
        healthy: 'Healthy'
    };
    return labels[riskLevel] || 'Unknown';
}

/**
 * Get severity color for vulnerability display.
 * Uses semantic --severity-* CSS variables that are defined per-theme
 * in themeVariations.css (WCAG AA for light themes, high-contrast for dark).
 * @param {string} severity - 'critical', 'high', 'medium', 'low', 'info'
 * @returns {string} - CSS color value
 */
export function getSeverityColor(severity) {
    const colors = {
        critical: 'var(--severity-critical)',
        high: 'var(--severity-high)',
        medium: 'var(--severity-medium)',
        low: 'var(--severity-low)',
        info: 'var(--severity-info)'
    };
    return colors[severity?.toLowerCase()] || 'var(--text-secondary)';
}

/**
 * Get trend icon and color
 * @param {string} trend - 'improving', 'stable', 'worsening'
 * @returns {object} - { icon, color, label }
 */
export function getTrendIndicator(trend) {
    const indicators = {
        improving: { icon: '↑', color: 'var(--risk-healthy, var(--severity-low))', label: 'Improving' },
        stable: { icon: '→', color: 'var(--text-secondary)', label: 'Stable' },
        worsening: { icon: '↓', color: 'var(--risk-critical, var(--severity-critical))', label: 'Worsening' }
    };
    return indicators[trend] || indicators.stable;
}

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date
 */
export function formatDate(dateString) {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

/**
 * Format date with time for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date with time
 */
export function formatDateTime(dateString) {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string} dateString - ISO date string
 * @returns {string} - Relative time string
 */
export function formatRelativeTime(dateString) {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
}

// Default export for convenience
const commandCenterService = {
    getCommandCenterSummary,
    getRiskLevelColor,
    getRiskLevelLabel,
    getSeverityColor,
    getTrendIndicator,
    formatDate,
    formatDateTime,
    formatRelativeTime
};

export default commandCenterService;
